import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import YouTubePlayer from "youtube-player"
import * as PlayerLogic from "../lib/player_logic"

// The single video player for both contexts (see
// docs/features/v1/video-player.md). Segment bounds present vs. absent is the
// only input difference:
//   - lesson context  (trackingMode 'lesson'): segment-aware course player —
//     custom chrome, segment capping, resume + LessonProgress writes.
//   - watch context    (trackingMode 'playback'): full video, native controls,
//     no resume, VideoPlayback analytics (mirrors the legacy video_controller).
// All decision logic lives in lib/player_logic.js (unit-tested in
// spec/javascript/player_logic.test.js) so this consolidation is guarded.
export default class extends Controller {
  static values = {
    courseUuid: String,
    lessonId: Number,
    videoIdentifier: String,
    videoDbId: Number,
    lastPosition: Number,
    nextLessonUrl: String,
    studyMode: { type: Boolean, default: true },
    currentLessonIntroViewed: Boolean,
    startTime: { type: Number, default: 0 },
    endTime: { type: Number, default: 0 },
    segmentMode: { type: Boolean, default: true },
    videoDuration: { type: Number, default: 0 },
    trackingMode: { type: String, default: 'lesson' }
  }

  static targets = ["stage", "speedButton", "currentSpeed", "playPauseButton", "playIcon", "pauseIcon", "stepForwardButton", "stepBackButton", "studyPanel", "studyModeStatus", "currentSpeedDisplay", "scrubber", "scrubberFill", "scrubberThumb", "scrubberPreDisabled", "scrubberPostDisabled", "timeStart", "timeEnd"]

  initialize() {
    this.progressInterval = null
    this.scrubberInterval = null
    this.isDragging = false
    this.scrubberDragMoveBound = null
    this.scrubberDragEndBound = null
    this.scrubberTouchStartBound = null
    this.playbackSpeed = 1.0
    this.hasResumed = false
    this.isPlaying = false
    this.frameStep = 0.033 // Approximate 1 frame at 30fps
    this.playerInitialized = false
    // Watch-context (playback mode) analytics state
    this.current = {}
    this.lastStopTime = 0
  }

  connect() {
    // Initialise the player. The lesson context gates on the intro having been
    // viewed; the standalone watch context has no intro, so init immediately.
    if (this.isPlaybackMode() || this.currentLessonIntroViewedValue) {
      this.initializePlayer()
    }

    // Handle page unload
    this.handleWindowUnload = this.handleWindowUnload.bind(this)
    window.addEventListener('beforeunload', this.handleWindowUnload)

    // Listen for replay / watch-full events from the interstitial (lesson context)
    this.handleReplayBound = this.handleReplay.bind(this)
    this.element.addEventListener('lesson-interstitial:replay', this.handleReplayBound)
    this.handleWatchFullBound = this.handleWatchFull.bind(this)
    this.element.addEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)

    // Add touch listener for scrubber with { passive: false } so preventDefault() works
    if (this.hasScrubberTarget) {
      this.scrubberTouchStartBound = this.scrubberTouchStart.bind(this)
      this.scrubberTarget.addEventListener('touchstart', this.scrubberTouchStartBound, { passive: false })
    }

    // Populate time labels immediately using values available from the DOM
    this.updateScrubberLabels()
    this.updateCurrentTimeLabel(this.startTimeValue || 0)

    this.updateStudyModeDisplay()
  }

  initializePlayer() {
    if (this.playerInitialized) return
    this.playerInitialized = true

    const playerVars = this.isPlaybackMode()
      ? {}
      : {
          autoplay: 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3
        }

    const player = YouTubePlayer(this.stageTarget, {
      width: '100%',
      height: '100%',
      playerVars
    })

    player.on('ready', e => {
      this.youtube = e.target
    })

    player.on('stateChange', e => {
      const time = e.target.playerInfo.currentTime

      // Watch context: analytics only, no segment chrome.
      if (this.isPlaybackMode()) {
        this.handlePlaybackStateChange(e, time)
        return
      }

      // --- Lesson context ---

      // VIDEO_CUED — video loaded but not yet playing; duration is available
      if (e.data === 5) {
        this.tryInitScrubber()
      }

      // PLAYING
      if (e.data === 1) {
        this.isPlaying = true
        this.updatePlayPauseButton()
        this.startProgressTracking()
        // Try to init scrubber duration on first play (fallback if VIDEO_CUED didn't fire)
        if (!this.videoDurationValue) {
          this.tryInitScrubber()
        }
      }

      // PAUSED
      if (e.data === 2) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)
        this.updateScrubber(time)
        // Enforce segment boundaries on seek-while-paused via native controls
        const correctedTime = PlayerLogic.seekWhilePausedTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
        if (correctedTime !== null) {
          this.youtube.seekTo(correctedTime, true)
        }
      }

      // ENDED
      if (e.data === 0) {
        this.isPlaying = false
        this.updatePlayPauseButton()
        this.stopProgressTracking()
        this.updateProgress(time)

        // Show completion interstitial instead of auto-advancing
        this.showCompletionInterstitial()
      }
    })

    // Load video with smart start position respecting segment window (lesson
    // context only; the watch context always starts at 0, no resume).
    const loadOptions = { videoId: this.videoIdentifierValue }
    if (!this.isPlaybackMode()) {
      const startSeconds = PlayerLogic.resumeStartSeconds(this.lastPositionValue, this.startTimeValue, this.endTimeValue)
      if (startSeconds !== undefined) {
        loadOptions.startSeconds = startSeconds
      }
    }
    player.loadVideoById(loadOptions)
  }

  disconnect() {
    this.stopProgressTracking()
    if (this.handleWindowUnload) {
      window.removeEventListener('beforeunload', this.handleWindowUnload)
    }
    if (this.handleReplayBound) {
      this.element.removeEventListener('lesson-interstitial:replay', this.handleReplayBound)
    }
    if (this.handleWatchFullBound) {
      this.element.removeEventListener('lesson-interstitial:watchFull', this.handleWatchFullBound)
    }
    if (this.scrubberTouchStartBound && this.hasScrubberTarget) {
      this.scrubberTarget.removeEventListener('touchstart', this.scrubberTouchStartBound)
    }
    // Remove any active drag listeners
    if (this.scrubberDragMoveBound) {
      document.removeEventListener('mousemove', this.scrubberDragMoveBound)
      document.removeEventListener('touchmove', this.scrubberDragMoveBound)
    }
    if (this.scrubberDragEndBound) {
      document.removeEventListener('mouseup', this.scrubberDragEndBound)
      document.removeEventListener('touchend', this.scrubberDragEndBound)
    }
  }

  isPlaybackMode() {
    return this.trackingModeValue === 'playback'
  }

  // --- Watch-context analytics (mirrors the legacy video_controller) ---

  handlePlaybackStateChange(e, time) {
    // PLAYING
    if (e.data === 1) {
      const startTime = PlayerLogic.resolvePlayStartTime(time, this.lastStopTime)
      this.current = { video_id: this.videoDbIdValue, start_time: PlayerLogic.toTenth(startTime) }
    }

    // PAUSED or ENDED
    if (e.data === 2 || e.data === 0) {
      this.current.end_time = PlayerLogic.toTenth(time)
      this.lastStopTime = time
      this.fireEvent()
    }
  }

  fireEvent() {
    post(document.body.dataset.playbacksUrl, {
      responseKind: 'json',
      body: this.current
    })
  }

  handlePlaybackUnload() {
    // Save the in-flight playback range if the player is still playing.
    if (this.youtube && this.current && this.current.start_time !== undefined) {
      const playerState = this.youtube.getPlayerState()
      // YouTube player state 1 = playing
      if (playerState === 1) {
        const currentTime = this.youtube.getCurrentTime()
        this.current.end_time = PlayerLogic.toTenth(currentTime)

        // Use sendBeacon for reliable delivery during page unload
        if (navigator.sendBeacon) {
          const formData = new FormData()
          formData.append('video_id', this.current.video_id)
          formData.append('start_time', this.current.start_time)
          formData.append('end_time', this.current.end_time)

          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          if (csrfToken) {
            formData.append('authenticity_token', csrfToken)
          }

          navigator.sendBeacon(document.body.dataset.playbacksUrl, formData)
        } else {
          this.fireEvent()
        }
      }
    }
  }

  startProgressTracking() {
    // Update progress every 10 seconds
    if (!this.progressInterval) {
      this.progressInterval = setInterval(() => {
        if (this.youtube && this.youtube.getCurrentTime) {
          try {
            // YouTube iframe API getCurrentTime() is synchronous
            const time = this.youtube.getCurrentTime()
            if (typeof time === 'number') {
              this.updateProgress(time)
            }
          } catch (error) {
            console.warn('Error tracking progress:', error)
          }
        }
      }, 10000) // 10 seconds
    }

    // Poll scrubber and end-time boundary at 100ms
    if (!this.scrubberInterval) {
      this.scrubberInterval = setInterval(() => {
        if (this.youtube && this.youtube.getCurrentTime) {
          try {
            const current = this.youtube.getCurrentTime()
            if (typeof current === 'number') {
              this.updateScrubber(current)
              const action = PlayerLogic.playbackBoundaryAction(current, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
              if (action.type === 'seek') {
                this.youtube.seekTo(action.target, true)
              } else if (action.type === 'end') {
                this.stopProgressTracking()
                this.youtube.pauseVideo()
                this.updateProgress(this.endTimeValue)
                this.showCompletionInterstitial()
              }
            }
          } catch (error) {
            console.warn('Error in scrubber interval:', error)
          }
        }
      }, 100)
    }
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    if (this.scrubberInterval) {
      clearInterval(this.scrubberInterval)
      this.scrubberInterval = null
    }
  }

  // --- Scrubber helpers ---

  isSegmentMode() {
    return PlayerLogic.isSegmentMode(this.segmentModeValue, this.endTimeValue)
  }

  tryInitScrubber() {
    if (!this.youtube || !this.youtube.getDuration) return
    try {
      const duration = this.youtube.getDuration()
      if (typeof duration === 'number' && duration > 0) {
        this.videoDurationValue = duration
        this.updateScrubberLabels()
      }
    } catch (error) {
      console.warn('Error getting video duration:', error)
    }
  }

  updateScrubberLabels() {
    if (this.hasTimeEndTarget) {
      this.timeEndTarget.textContent = this.formatTime(this.videoDurationValue)
    }
    this.updateDisabledZones()
  }

  updateCurrentTimeLabel(time) {
    if (this.hasTimeStartTarget) {
      this.timeStartTarget.textContent = this.formatTime(time)
    }
  }

  updateScrubber(currentTime) {
    const position = PlayerLogic.scrubberPosition(currentTime, this.videoDurationValue)
    this.updateScrubberPositionUI(position)
    this.updateCurrentTimeLabel(currentTime)
  }

  updateScrubberPositionUI(position) {
    const pct = PlayerLogic.pctString(position)
    if (this.hasScrubberFillTarget) {
      this.scrubberFillTarget.style.width = pct
    }
    if (this.hasScrubberThumbTarget) {
      this.scrubberThumbTarget.style.left = pct
    }
  }

  scrubberMouseDown(event) {
    event.preventDefault()
    this.isDragging = true
    this.wasPlayingBeforeDrag = this.isPlaying
    if (this.isPlaying) this.youtube.pauseVideo()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    this.scrubberDragMoveBound = this.scrubberDragMove.bind(this)
    this.scrubberDragEndBound = this.scrubberDragEnd.bind(this)
    document.addEventListener('mousemove', this.scrubberDragMoveBound)
    document.addEventListener('mouseup', this.scrubberDragEndBound)
  }

  scrubberTouchStart(event) {
    event.preventDefault()
    this.isDragging = true
    this.wasPlayingBeforeDrag = this.isPlaying
    if (this.isPlaying) this.youtube.pauseVideo()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    this.scrubberDragMoveBound = this.scrubberDragMove.bind(this)
    this.scrubberDragEndBound = this.scrubberDragEnd.bind(this)
    document.addEventListener('touchmove', this.scrubberDragMoveBound, { passive: false })
    document.addEventListener('touchend', this.scrubberDragEndBound)
  }

  scrubberDragMove(event) {
    if (!this.isDragging) return
    event.preventDefault()
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
  }

  scrubberDragEnd(event) {
    if (!this.isDragging) return
    this.isDragging = false
    const position = this.positionFromEvent(event)
    this.updateScrubberPositionUI(position)
    this.seekToPosition(position)
    if (this.wasPlayingBeforeDrag) this.youtube.playVideo()
    document.removeEventListener('mousemove', this.scrubberDragMoveBound)
    document.removeEventListener('mouseup', this.scrubberDragEndBound)
    document.removeEventListener('touchmove', this.scrubberDragMoveBound)
    document.removeEventListener('touchend', this.scrubberDragEndBound)
    this.scrubberDragMoveBound = null
    this.scrubberDragEndBound = null
  }

  positionFromEvent(event) {
    if (!this.hasScrubberTarget) return 0
    const rect = this.scrubberTarget.getBoundingClientRect()
    let clientX
    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX
    } else {
      clientX = event.clientX
    }
    return PlayerLogic.positionFromClientX(clientX, rect.left, rect.width)
  }

  seekToPosition(position) {
    if (!this.youtube) return
    const targetTime = PlayerLogic.seekPositionToTime(position, this.videoDurationValue, this.startTimeValue, this.endTimeValue, this.isSegmentMode())
    this.updateCurrentTimeLabel(targetTime)
    try {
      this.youtube.seekTo(targetTime, true)
    } catch (error) {
      console.error('Error seeking:', error)
    }
  }

  switchToFullMode() {
    this.segmentModeValue = false
    this.updateScrubberLabels()
  }

  updateDisabledZones() {
    const zones = PlayerLogic.disabledZones(this.startTimeValue, this.endTimeValue, this.videoDurationValue, this.isSegmentMode())
    if (this.hasScrubberPreDisabledTarget) {
      if (zones.preWidth !== null) this.scrubberPreDisabledTarget.style.width = zones.preWidth
      this.scrubberPreDisabledTarget.classList.toggle('hidden', zones.preHidden)
    }
    if (this.hasScrubberPostDisabledTarget) {
      if (zones.postWidth !== null) this.scrubberPostDisabledTarget.style.width = zones.postWidth
      this.scrubberPostDisabledTarget.classList.toggle('hidden', zones.postHidden)
    }
  }

  formatTime(seconds) {
    return PlayerLogic.formatTime(seconds)
  }

  // --- End scrubber helpers ---

  updateProgress(position) {
    const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`

    post(url, {
      responseKind: 'json',
      body: {
        position: position,
        watched_duration: 10
      }
    }).then(response => {
      if (response.ok) {
        response.json.then(data => {
          // Dispatch event for chapter navigation to update UI
          if (data.completed) {
            this.dispatch('lessonCompleted', { detail: { lessonId: this.lessonIdValue } })
          }

          // Update course progress bar if it exists
          this.dispatch('progressUpdated', {
            detail: {
              courseCompletion: data.course_completion_percentage
            }
          })
        })
      }
    })
  }

  handleWindowUnload() {
    // Watch context: persist the in-flight VideoPlayback range.
    if (this.isPlaybackMode()) {
      this.handlePlaybackUnload()
      return
    }

    // Lesson context: save progress on page unload
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        // YouTube iframe API getCurrentTime() is synchronous, not a Promise
        const time = this.youtube.getCurrentTime()

        if (typeof time === 'number' && time > 0) {
          const formData = new FormData()
          formData.append('position', time)
          formData.append('watched_duration', 10)

          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          if (csrfToken) {
            formData.append('authenticity_token', csrfToken)
          }

          const url = `/courses/${this.courseUuidValue}/lessons/${this.lessonIdValue}/update_progress`
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, formData)
          }
        }
      } catch (error) {
        // Silently fail during page unload to avoid console errors
        console.warn('Could not save progress on unload:', error)
      }
    }
  }

  // Playback speed controls
  setSpeed(speed) {
    this.playbackSpeed = speed
    if (this.youtube) {
      try {
        this.youtube.setPlaybackRate(speed)

        // Auto-mute at slow speeds (audio becomes inaudible/distorted)
        if (PlayerLogic.speedShouldMute(speed)) {
          this.youtube.mute()
        } else {
          this.youtube.unMute()
        }
      } catch (error) {
        console.error('Error setting playback rate:', error)
      }
    }
    this.updateSpeedDisplay()
  }

  speed025(event) {
    event.preventDefault()
    this.setSpeed(0.25)
  }

  speed050(event) {
    event.preventDefault()
    this.setSpeed(0.5)
  }

  speed100(event) {
    event.preventDefault()
    this.setSpeed(1.0)
  }

  updateSpeedDisplay() {
    if (this.hasCurrentSpeedTarget) {
      this.currentSpeedTarget.textContent = `${this.playbackSpeed}x`
    }

    // Update current speed display in Study Mode panel
    if (this.hasCurrentSpeedDisplayTarget) {
      this.currentSpeedDisplayTarget.textContent = `${this.playbackSpeed}x`
    }

    // Update button states
    this.speedButtonTargets.forEach(button => {
      const speed = parseFloat(button.dataset.speed)
      if (speed === this.playbackSpeed) {
        button.classList.add('bg-blue-600', 'text-white')
        button.classList.remove('bg-gray-200', 'text-gray-700')
      } else {
        button.classList.remove('bg-blue-600', 'text-white')
        button.classList.add('bg-gray-200', 'text-gray-700')
      }
    })
  }

  // Skip controls
  skipForward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(PlayerLogic.skipForwardTarget(time, this.endTimeValue, this.isSegmentMode()), true)
      } catch (error) {
        console.error('Error skipping forward:', error)
      }
    }
  }

  skipBackward(event) {
    event.preventDefault()
    if (this.youtube && this.youtube.getCurrentTime) {
      try {
        const time = this.youtube.getCurrentTime()
        this.youtube.seekTo(PlayerLogic.skipBackwardTarget(time, this.startTimeValue, this.isSegmentMode()), true)
      } catch (error) {
        console.error('Error skipping backward:', error)
      }
    }
  }

  // Play/Pause control
  togglePlayPause(event) {
    event.preventDefault()
    if (!this.youtube) return

    try {
      if (this.isPlaying) {
        this.youtube.pauseVideo()
      } else {
        this.youtube.playVideo()
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error)
    }
  }

  updatePlayPauseButton() {
    if (!this.hasPlayPauseButtonTarget) return

    if (this.isPlaying) {
      // Show pause icon, hide play icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.add('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.remove('hidden'))
      // Disable frame step buttons when playing (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = true)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = true)
      }
    } else {
      // Show play icon, hide pause icon on all play/pause buttons
      this.playIconTargets.forEach(icon => icon.classList.remove('hidden'))
      this.pauseIconTargets.forEach(icon => icon.classList.add('hidden'))
      // Enable frame step buttons when paused (affects all frame step buttons)
      if (this.hasStepForwardButtonTarget) {
        this.stepForwardButtonTargets.forEach(btn => btn.disabled = false)
      }
      if (this.hasStepBackButtonTarget) {
        this.stepBackButtonTargets.forEach(btn => btn.disabled = false)
      }
    }
  }

  // Frame stepping (only works when paused)
  stepForward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(PlayerLogic.stepForwardTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode(), this.frameStep), true)
    } catch (error) {
      console.error('Error stepping forward:', error)
    }
  }

  stepBackward(event) {
    event.preventDefault()
    if (!this.youtube || this.isPlaying) return

    try {
      const time = this.youtube.getCurrentTime()
      this.youtube.seekTo(PlayerLogic.stepBackwardTarget(time, this.startTimeValue, this.endTimeValue, this.isSegmentMode(), this.frameStep), true)
    } catch (error) {
      console.error('Error stepping backward:', error)
    }
  }

  // Study Mode toggle
  toggleStudyMode(event) {
    event.preventDefault()
    this.studyModeValue = !this.studyModeValue
    this.updateStudyModeDisplay()
  }

  updateStudyModeDisplay() {
    const isStudyMode = this.studyModeValue

    // Slide panel up/down
    if (this.hasStudyPanelTarget) {
      if (isStudyMode) {
        this.studyPanelTarget.style.maxHeight = this.studyPanelTarget.scrollHeight + 'px'
        // Remove overflow-hidden after animation completes to allow popover to show
        setTimeout(() => {
          if (this.studyModeValue) { // Check again in case it was toggled quickly
            this.studyPanelTarget.style.overflow = 'visible'
          }
        }, 300) // Match transition duration
      } else {
        // Add overflow-hidden back for collapse animation
        this.studyPanelTarget.style.overflow = 'hidden'
        this.studyPanelTarget.style.maxHeight = '0'
      }
    }

    // Update status text with neon glow
    if (this.hasStudyModeStatusTarget) {
      this.studyModeStatusTarget.textContent = isStudyMode ? 'On' : 'Off'
      // Both On and Off states have purple glow
      this.studyModeStatusTarget.style.textShadow = '0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)'
    }
  }

  showCompletionInterstitial() {
    const interstitialModal = document.querySelector('[data-controller="lesson-interstitial"]')
    if (interstitialModal) {
      // Get the Stimulus controller instance and call show() method
      const stimulus = window.Stimulus || this.application
      const interstitialController = stimulus.getControllerForElementAndIdentifier(
        interstitialModal,
        'lesson-interstitial'
      )

      if (interstitialController) {
        interstitialController.show()
      } else {
        // Fallback if controller not found
        interstitialModal.classList.remove('hidden')
        document.body.style.overflow = 'hidden'
      }
    }
  }

  handleReplay(event) {
    if (this.youtube) {
      // Restore segment mode if this lesson has a segment
      if (this.endTimeValue > 0) {
        this.segmentModeValue = true
        this.updateScrubberLabels()
      }
      this.youtube.seekTo(this.startTimeValue || 0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  handleWatchFull(event) {
    if (this.youtube) {
      this.switchToFullMode()
      this.youtube.seekTo(0, true)
      this.youtube.playVideo()
      this.isPlaying = true
      this.updatePlayPauseButton()
    }
  }

  get player() {
    return this.youtube
  }
}
